import './BootstrapDropdown.scss';
import cn from 'classnames';
import {
  ComponentProps,
  Dispatch,
  ReactElement,
  ReactNode,
  Ref,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Dropdown as BSDropdown, DropdownProps as RBDropdownProps } from 'react-bootstrap';
import DropdownContext from '@restart/ui/DropdownContext';
import { useOnClose } from '../../utils/hooks/useOnClose';
import { Placement, PlacementType, Size, StretchMode } from '../designConstants';
import {
  DropdownSharedProps,
  DropdownToggleType,
  MenuItem,
  MultiMenuSections,
  SubmenuControlProps
} from './dropdown-constants';
import { getSubmenuSummary, isMultiMenuItems } from './dropdown-submenu-utils';
import { ToolbarSizeReset } from '../Toolbar';
import { DropdownMenu, DropdownMenuProps } from './DropdownMenu';
import { DropdownToggle } from './DropdownToggle';

// Keys BootstrapDropdown sets on <BSDropdown> itself, or exposes under its own prop names.
type DropdownReactBootstrapPassthroughProps = Omit<
  RBDropdownProps,
  'className' | 'show' | 'onToggle' | 'onSelect' | 'children' | 'align' | 'bsPrefix' | 'as'
>;

export interface BootstrapDropdownProps<T extends string>
  extends
    DropdownSharedProps<T>,
    Omit<
      DropdownMenuProps<T>,
      'className' | 'itemOnClick' | 'menuItems' | 'size' | keyof SubmenuControlProps<T>
    > {
  // Looks like an enabled dropdown but is inert: no native `disabled` (so no grey-out), the menu
  // never opens and the toggle is skipped by Tab. For single-option pickers. Only this variant
  // implements it, so it is not on the shared props.
  readOnly?: boolean;
  searchable?: false;
  submenuValue?: never;
  onSubmenuChange?: never;
  creatable?: never;
  optionFetcher?: never;
  variant?: never;
  // Widens the menu past the toggle so every option label fits on one line.
  fitMenuToContent?: boolean;
  reactSelectProps?: never;
  reactBootstrapProps?: DropdownReactBootstrapPassthroughProps;
  value?: T;
  ariaLabel?: string;
  overlayPlacement?: PlacementType;
  // Opt-in ref on the value span (not the placeholder), for pairing with
  // useIsTruncated to detect ellipsis truncation.
  valueRef?: Ref<HTMLSpanElement>;
  onChange: Dispatch<T>;
  show?: boolean;
  setShow?: Dispatch<SetStateAction<boolean>>;
  onToggle?: ComponentProps<typeof BSDropdown>['onToggle'];
  autoFill?: boolean;
  dropdownMenuClassName?: string;
  menuRenderOverride?: Record<string, 'stringList'>;
  multiMenuSections?: MultiMenuSections;
  menuSectionTitles?: Record<string, ReactElement>;
  menuItems?: MenuItem<T>[] | MenuItem<T>[][];
  itemOnClick?: (item: MenuItem<T>) => void;
  applyDisabled?: boolean;
  // A single value applies to every menu column; an array is indexed by menu (column)
  // index for dropdowns that mix a single-select column with a multi-select one.
  multiSelect?: boolean | boolean[];
  valueContent?: ReactNode;
}

// Options with `children` render as an inline, one-tier submenu inside DropdownMenu.
export type BootstrapSubmenuProps<T extends string> = Omit<
  BootstrapDropdownProps<T>,
  'clearable' | 'onChange' | 'onClear' | 'value' | keyof SubmenuControlProps<T>
> &
  SubmenuControlProps<T>;

export const BootstrapDropdown = <T extends string>({
  type = DropdownToggleType.TEXT_INPUT,
  label,
  ariaLabel,
  value,
  valueRef,
  options = [],
  onChange,
  leadingIcon,
  size = Size.MD,
  disabled,
  placeholder,
  overlay,
  overlayPlacement = Placement.AUTO,
  hasError,
  onMenuClose,
  menuAlign,
  menuStrategy,
  itemsRef,
  className,
  show: showInput,
  setShow: setShowInput,
  onToggle: onToggleInput,
  autoFill,
  applyContent,
  applyOnClick,
  applyDisabled,
  cancelContent,
  cancelOnClick,
  collapsibleSections,
  filterBarPlaceholder,
  filterPills,
  footer,
  formatOptionLabel,
  section,
  multiMenuSections,
  footerOnClick,
  hasFilterBar,
  hasTicks: hasTicksProp,
  hideApplyOnClick,
  hideCancelOnClick,
  id,
  itemOnClick,
  menuItems,
  multiSelect,
  onSubmenuChange,
  submenuValue,
  subTitles,
  title,
  dropdownMenuClassName,
  fitMenuToContent = false,
  menuRenderOverride,
  menuSectionTitles,
  hideCaret = false,
  readOnly = false,
  clearable = false,
  onClear,
  valueContent,
  stretch = StretchMode.FILL,
  shouldTruncateOptions = false,
  virtualize,
  reactBootstrapProps
}: Omit<BootstrapDropdownProps<T>, 'searchable' | keyof SubmenuControlProps<T>> &
  Partial<SubmenuControlProps<T>>): ReactElement => {
  const isTitleType =
    type === DropdownToggleType.WIDGET_TITLE || type === DropdownToggleType.PAGE_TITLE;
  // Let DropdownMenu compute its own default when the caller doesn't specify one — it
  // additionally considers applyOnClick/hasItemOptions/multiMenu, not just multiSelect.
  const hasTicks = hasTicksProp;
  // Derive `selected` from `value` rather than the item's own `selected` field.
  // DropdownMenu mutates item.selected on row click, so without this re-deriving
  // it on each render, the bold styling sticks even after the value is cleared.
  const items = useMemo(
    () =>
      options.map(option => {
        if (typeof option === 'string') {
          return { label: option, value: option, selected: option === value };
        }
        return {
          ...option,
          label: option.label || option.value,
          selected: option.value === value
        };
      }),
    [options, value]
  );

  const submenuSummary = useMemo(() => {
    const sourceItems = menuItems ?? items;
    if (!submenuValue || isMultiMenuItems(sourceItems)) return undefined;
    const summary = getSubmenuSummary(sourceItems, submenuValue);
    return summary && <span className='dropdown-value'>{summary}</span>;
  }, [menuItems, items, submenuValue]);

  const [show, setShow] = useState(false);

  const onToggle: ComponentProps<typeof BSDropdown>['onToggle'] =
    onToggleInput ||
    ((isOpen, _meta) => {
      if (setShowInput != null) {
        setShowInput(isOpen);
      } else {
        setShow(isOpen);
      }
    });

  // Restores close-on-select for single-select dropdowns. Before the shared Menu
  // primitive landed, react-bootstrap's <Dropdown.Item> closed the menu on click;
  // now DropdownMenu calls this from handleRowClick when no apply/cancel buttons
  // are wired (multi-select still relies on the Apply button to close).
  const closeMenuFromSelect = useCallback(() => {
    if (onToggleInput) {
      onToggleInput(false, { source: 'select', originalEvent: undefined });
    } else if (setShowInput != null) {
      setShowInput(false);
    } else {
      setShow(false);
    }
  }, [onToggleInput, setShowInput]);

  const menu = useMemo(
    () => (
      <ToolbarSizeReset>
        <DropdownMenu
          className={cn('dropdown-menu', dropdownMenuClassName, {
            'fit-content': fitMenuToContent
          })}
          menuAlign={menuAlign}
          menuStrategy={menuStrategy}
          itemsRef={itemsRef}
          menuItems={menuItems || items}
          itemOnClick={(item: MenuItem<T>) => {
            if (onChange) {
              onChange(item.value);
            }
            itemOnClick?.(item);
          }}
          applyContent={applyContent}
          applyOnClick={applyOnClick}
          applyDisabled={applyDisabled}
          cancelContent={cancelContent}
          cancelOnClick={cancelOnClick}
          collapsibleSections={collapsibleSections}
          hideCancelOnClick={hideCancelOnClick}
          hideApplyOnClick={hideApplyOnClick}
          filterBarPlaceholder={filterBarPlaceholder}
          filterPills={filterPills}
          footer={footer}
          formatOptionLabel={formatOptionLabel}
          footerOnClick={footerOnClick}
          hasFilterBar={hasFilterBar}
          hasTicks={hasTicks}
          id={id}
          multiSelect={multiSelect}
          onSubmenuChange={onSubmenuChange}
          submenuValue={submenuValue}
          section={section}
          multiMenuSections={multiMenuSections}
          subTitles={subTitles}
          title={title}
          menuRenderOverride={menuRenderOverride}
          menuSectionTitles={menuSectionTitles}
          shouldTruncateOptions={shouldTruncateOptions}
          virtualize={virtualize}
          onClose={closeMenuFromSelect}
        />
      </ToolbarSizeReset>
    ),
    [
      applyContent,
      applyOnClick,
      applyDisabled,
      cancelContent,
      cancelOnClick,
      collapsibleSections,
      closeMenuFromSelect,
      filterBarPlaceholder,
      filterPills,
      footer,
      formatOptionLabel,
      footerOnClick,
      hasFilterBar,
      hasTicks,
      id,
      itemOnClick,
      items,
      menuItems,
      multiSelect,
      onChange,
      onSubmenuChange,
      submenuValue,
      subTitles,
      title,
      section,
      dropdownMenuClassName,
      fitMenuToContent,
      menuRenderOverride,
      multiMenuSections,
      menuSectionTitles,
      hideCancelOnClick,
      hideApplyOnClick,
      menuAlign,
      menuStrategy,
      itemsRef,
      shouldTruncateOptions,
      virtualize
    ]
  );

  useEffect(() => {
    if (autoFill && items && items.length === 1 && value !== items[0].value) {
      onChange(items[0].value);
    }
  }, [autoFill, items, onChange, value]);

  const currentShow = !readOnly && (showInput ?? show);

  useOnClose(currentShow, onMenuClose);

  return (
    <BSDropdown
      {...reactBootstrapProps}
      className={cn('bpm-dropdown', stretch, {
        'title-dropdown': isTitleType,
        'icon-only-dropdown': type === DropdownToggleType.ICON_ONLY
      })}
      show={currentShow}
      onToggle={readOnly ? undefined : onToggle}
    >
      <DropdownToggleWithContextRef<T>
        type={type}
        label={label}
        ariaLabel={ariaLabel}
        value={value}
        valueRef={valueRef}
        valueContent={valueContent ?? submenuSummary}
        options={options}
        leadingIcon={leadingIcon}
        size={size}
        disabled={disabled}
        placeholder={placeholder}
        overlay={overlay}
        overlayPlacement={overlayPlacement}
        className={className}
        hasError={hasError}
        hideCaret={hideCaret}
        readOnly={readOnly}
        clearable={clearable}
        onClear={onClear}
        onToggleClick={() =>
          onToggle?.(!currentShow, { source: 'click', originalEvent: undefined })
        }
        isOpen={currentShow}
      />
      {menu}
    </BSDropdown>
  );
};

// Pulls react-bootstrap's setToggle ref out of DropdownContext and attaches it
// to the underlying button so popper can use the toggle as its positioning
// reference. Required for menuAlign='end' (and any other popper-driven layout
// behavior) because our custom DropdownToggle bypasses <BSDropdown.Toggle>.
const DropdownToggleWithContextRef = <T extends string>(
  props: Omit<ComponentProps<typeof DropdownToggle<T>>, 'buttonRef'>
): ReactElement => {
  const ctx = useContext(DropdownContext);
  return <DropdownToggle<T> {...props} buttonRef={ctx?.setToggle} />;
};
